"use client";

import { FeedRidContext, PrematchUpcomingFeedContext } from "@finbackoffice/site-core";
import { ConfigContextV2 } from "contexts";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { HoursRange } from "@finbackoffice/websocket-client";
import Loading from "components/base/loading/Loading";
import SportGamesContent from "components/home/sport-games/content/SportGamesContent";
import { useSocketStorage, useSportFeedType } from "hooks";
import { FeedScopeEnum } from "utils/constants";
import { PrematchContext } from "contexts";
import { sortSports } from "@finbackoffice/fe-core";
import { usePrematchUpcomingSports } from "hooks/sportsbook/usePrematchUpcomingSports";

type IProps = {
    hoursRange: HoursRange;
    sliderSpeed: number;
};

const PrematchWidget: FC<IProps> = ({ sliderSpeed, hoursRange }) => {
    usePrematchUpcomingSports(hoursRange);
    const { sportsOrder } = useContext(ConfigContextV2);

    const { prematchUpcomingFeed, prematchUpcomingIsLoading, hasPrematchUpcomingSports } =
        useContext(PrematchUpcomingFeedContext);
    const { resetHoursRange } = useContext(PrematchContext);
    const { prematchUpcomingRid } = useContext(FeedRidContext);

    const [activeSportId, setActiveSportId] = useState<number | null>(null);

    const { getEventsBySportId } = useSocketStorage(prematchUpcomingRid);
    const { isBCFeed } = useSportFeedType();

    useEffect(() => {
        if (!prematchUpcomingIsLoading && hasPrematchUpcomingSports) {
            const selectedSportDoesNotExist =
                activeSportId &&
                prematchUpcomingFeed.findIndex((sport) => sport.id === activeSportId) === -1;

            if (prematchUpcomingFeed.length && (!activeSportId || selectedSportDoesNotExist)) {
                if (isBCFeed) {
                    setActiveSportId(prematchUpcomingFeed[0].id);
                } else {
                    setActiveSportId(
                        prematchUpcomingFeed.sort((a, b) => sortSports(sportsOrder)(a, b))[0].id,
                    );
                }
            }
        } else {
            setActiveSportId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prematchUpcomingFeed]);

    useEffect(() => {
        return () => {
            resetHoursRange();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSportClickHandler = useCallback((sportId: number) => {
        setActiveSportId(sportId);
    }, []);

    const activeSportEvents = useMemo(
        () => (activeSportId ? getEventsBySportId(activeSportId) : []),
        [activeSportId, getEventsBySportId],
    );

    if (prematchUpcomingIsLoading) return <Loading />;

    return (
        <SportGamesContent
            events={activeSportEvents}
            sports={prematchUpcomingFeed}
            activeSportId={activeSportId}
            onSportClickHandler={onSportClickHandler}
            autoplayDelay={sliderSpeed}
            feed={FeedScopeEnum.prematch}
        />
    );
};

export default PrematchWidget;
